import type { CartItem, PersistedGuestCart } from '../types/cart.types'
import { GUEST_CART_KEY, GUEST_CART_TTL_DAYS, GUEST_CART_VERSION } from '../types/cart.types'

const TTL_MS = GUEST_CART_TTL_DAYS * 24 * 60 * 60 * 1000

/**
 * Load guest cart from localStorage.
 * Returns [] on any failure (malformed JSON, version mismatch, expired, unavailable storage).
 */
export function loadGuestCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(GUEST_CART_KEY)
    if (!raw) return []

    const parsed: PersistedGuestCart = JSON.parse(raw)

    // Schema version mismatch — discard stale data
    if (parsed.version !== GUEST_CART_VERSION) {
      localStorage.removeItem(GUEST_CART_KEY)
      return []
    }

    // TTL check
    const age = Date.now() - new Date(parsed.persisted_at).getTime()
    if (age > TTL_MS) {
      localStorage.removeItem(GUEST_CART_KEY)
      return []
    }

    // Validate shape minimally — must be an array
    if (!Array.isArray(parsed.items)) return []

    return parsed.items
  } catch {
    // Corrupted storage — clean up silently
    try { localStorage.removeItem(GUEST_CART_KEY) } catch { /* storage unavailable */ }
    return []
  }
}

/**
 * Persist guest cart to localStorage.
 * Silently no-ops if localStorage is unavailable (private browsing quota).
 */
export function saveGuestCart(items: CartItem[]): void {
  try {
    const payload: PersistedGuestCart = {
      version: GUEST_CART_VERSION,
      items,
      persisted_at: new Date().toISOString(),
    }
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(payload))
  } catch {
    // Storage quota exceeded or unavailable — silent no-op
  }
}

/**
 * Remove guest cart from localStorage.
 * Called after successful guest→auth merge.
 */
export function clearGuestCartStorage(): void {
  try { localStorage.removeItem(GUEST_CART_KEY) } catch { /* ignore */ }
}

/**
 * Generates a stable guest item ID from product_id + optional variant_id.
 * Used to detect duplicates in the guest cart.
 */
export function guestItemKey(productId: string, variantId?: string): string {
  return variantId ? `${productId}::${variantId}` : productId
}